import React from "react";
import { Box, Paper, Typography, CircularProgress, Button } from "@material-ui/core";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import GeneratedImageDisplay from "./GeneratedImageDisplay";
import ReferenceImageThumbnail from "./ReferenceImageThumbnail";
import { ChatMessageData } from "./types";

interface ChatMessageProps {
  message: ChatMessageData;
  onUseImage: (messageId: string) => void;
  onUseAsReference: (messageId: string) => void;
}

const ChatMessage: React.FC<ChatMessageProps> = ({ message, onUseImage, onUseAsReference }) => {
  const { t } = useTranslation();
  const isUser = message.role === "user";

  return (
    <MessageContainer $isUser={isUser}>
      <MessageBubble elevation={1} $isUser={isUser}>
        {isUser ? (
          <>
            {message.content && (
              <Typography variant="body2" style={{ whiteSpace: "pre-wrap" }}>
                {message.content}
              </Typography>
            )}
            {message.referenceImageUrl && (
              <Box mt={1}>
                <ReferenceImageThumbnail imageUrl={message.referenceImageUrl} />
                <Box mt={1} display="flex" justifyContent="center">
                  <Button variant="outlined" onClick={() => onUseAsReference(message.id)} size="small">
                    {t("imageGenerator.useAsReference")}
                  </Button>
                </Box>
              </Box>
            )}
          </>
        ) : (
          <>
            {message.status === "pending" && (
              <Box display="flex" alignItems="center">
                <CircularProgress size={20} />
                <Box ml={1}>
                  <Typography variant="body2">{t("imageGenerator.generating")}</Typography>
                </Box>
              </Box>
            )}

            {message.status === "error" && (
              <Typography variant="body2" color="error">
                {message.error || t("imageGenerator.errorGenerating")}
              </Typography>
            )}

            {message.status === "completed" && message.imageUrl && message.imageBlob && (
              <GeneratedImageDisplay
                imageUrl={message.imageUrl}
                onUseImage={() => onUseImage(message.id)}
                onUseAsReference={() => onUseAsReference(message.id)}
              />
            )}
          </>
        )}
      </MessageBubble>
    </MessageContainer>
  );
};

export default ChatMessage;

const MessageContainer = styled(Box)<{ $isUser: boolean }>`
  display: flex;
  justify-content: ${props => (props.$isUser ? "flex-start" : "flex-end")};
  margin-bottom: 16px;
`;

const MessageBubble = styled(Paper)<{ $isUser: boolean }>`
  padding: 12px;
  max-width: 80%;
  background-color: ${props => (props.$isUser ? "#cfedffff" : "#eaeaeaff")};
`;
